import { useContext } from 'react'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { INTL_DMY_CASUAL_FORMAT, INTL_DAY_MONTH_NAME } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { formatDynamicAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import { extractOfferIdsFromOrder } from 'tripPlanner/components/EntryPoints/utils'
import BookingItemCard from 'tripPlanner/components/ItemCard/BookingItemCard'
import type { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import useOffers from 'hooks/Offers/useOffers'

interface Props {
  order: App.Order
  orderItem: App.OrderItem
  showBorder?: boolean
  onSelect?: (
    order: App.Order,
    offers: Array<OfferTypeForTripItemTypes>,
    checked: boolean,
  ) => void
}

function HotelBookingCard(props: Props) {
  const { order, orderItem, showBorder, onSelect } = props
  const items = order.items
  const item = items[0]

  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const intlDateFormatter = useIntlDateFormatter()

  const [offers, isOffersLoading] = useOffers(extractOfferIdsFromOrder(order))

  const getReservationDates = () => {
    let dateString = ''
    if (item.reservation?.startDate) {
      dateString += intlDateFormatter(
        item.reservation.startDate,
        INTL_DAY_MONTH_NAME,
      )
    }

    if (item.reservation?.endDate) {
      dateString += ` - ${intlDateFormatter(
        item.reservation.endDate,
        INTL_DMY_CASUAL_FORMAT,
      )}`
    }

    return dateString
  }

  if (isOffersLoading) {
    return <LoadingIndicator inline />
  }

  const offer = offers.find((offer) => offer.id === orderItem.offerId)

  const propertyName =
    offer && offer.type === 'hotel' && offer.property ?
      offer.property.name :
      item.offer.name

  return (
    <BookingItemCard
      heading={propertyName}
      image={orderItem?.offer.image}
      subHeading={getReservationDates()}
      price={formatDynamicAmountWithDollarType(
        orderItem.total,
        currentCurrency,
        currentRegionCode,
      )}
      showBorder={showBorder}
      onSelect={(checked) => {
        if (onSelect) {
          onSelect(order, offers, checked)
        }
      }}
    />
  )
}

export default HotelBookingCard
